// Unpublished Work © 2023 Deere & Company.
import { FuelToastVariant, useToastProvider } from "@deere/fuel-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteNotification,
  expireNotificationInStorage,
  filterNotificationsToDelete,
  getNotifications,
  markAllNotificationAsReadInStorage,
  markNotificationsAsReadInStorage,
  savePromotionDownloadNotification,
} from "./utils";
import { ConfigContext } from "../../../../../../../../context/ConfigContext";
import {
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
} from "../../../../../../../../utils/constants";
import {
  INV_DOWNLOAD_ID,
  NOTIFY,
} from "../../../../../../../../utils/eventConstants";
import {
  getDownloadFailureToastMessage,
  getDownloadSuccessToastMessage,
} from "../../../../../../../../utils/notificationsUtils";

export interface DownloadNotification {
  id: string;
  filename: string;
  displayName?: string;
  downloadUrl: string | null;
  compilationStatus: "COMPLETED" | "FAILED" | "PARTIAL_SUCCESS";
  createdDate?: string;
  type?: string;
  metadata?: any;
}

export interface Notification<T extends { id: string } = DownloadNotification> {
  createdAt: Date;
  isRead: boolean;
  isExpired: boolean;
  payload: T;
}

export interface AccountNotifications {
  notifications: Notification[];
  areNewNotificationsAvailable: boolean;
}

export interface UseNotificationResult {
  notifications: Notification[];
  areNewNotificationsAvailable: boolean;
  markAllNotificationAsRead: () => void;
  markNotificationsAsRead: (notificationIds: string[]) => void;
  expireNotification: (notificationId: number) => void;
  deleteNotification: (notificationIds: number) => void;
}

export interface UseNotificationProps {
  accountId: string;
  isMenuOpen: boolean;
}

const useNotifications = ({
  accountId,
  isMenuOpen,
}: UseNotificationProps): UseNotificationResult => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [areNewNotificationsAvailable, setAreNewNotificationsAvailable] =
    useState(false);

  const { enqueueToast } = useToastProvider();
  const { t, i18n } = useTranslation("common");
  const { DELETE_EXPIRED_NOTIFICATIONS, NOTIFICATION_EXPIRY_DAYS = 7 } =
    useContext(ConfigContext);

  useEffect(() => {
    const existingNotifications = getNotifications(accountId);
    setAreNewNotificationsAvailable(
      existingNotifications.areNewNotificationsAvailable
    );

    const [validNotifications, notificationsToDelete] =
      filterNotificationsToDelete(
        existingNotifications.notifications,
        NOTIFICATION_EXPIRY_DAYS
      );

    if (DELETE_EXPIRED_NOTIFICATIONS) {
      if (notificationsToDelete.length > 0) {
        deleteNotification(
          accountId,
          ...notificationsToDelete.map(({ createdAt }) =>
            new Date(createdAt).getTime()
          )
        );
      }
      setNotifications(validNotifications);
    } else {
      setNotifications(existingNotifications.notifications);
    }
  }, [accountId]);

  const newNotificationHandler = (
    e: Event,
    isNotificationMenuOpen: boolean
  ) => {
    const payload = (<CustomEvent>e).detail as DownloadNotification;
    const updatedAccountNotification = savePromotionDownloadNotification(
      accountId,
      payload,
      isNotificationMenuOpen
    );
    let filename;
    if (payload.filename && !payload.filename.startsWith(INV_DOWNLOAD_ID)) {
      filename = payload.filename;
    }

    let partialError;
    if (
      payload.type === INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION ||
      payload.type === INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION
    ) {
      const accountIdsWithErrors = payload.metadata.error
        ? payload.metadata.error.map(
            (errorMessage: any) => errorMessage.accountId
          )
        : [];
      if (accountIdsWithErrors && accountIdsWithErrors.length > 0) {
        partialError = t(
          "notifications.invoiceFileDownloadPartialFailureNotificationMessage"
        );
      }
    }

    const successMessage = getDownloadSuccessToastMessage(
      payload.type as string,
      partialError,
      t,
      payload.displayName || filename
    );

    const failureMessage = getDownloadFailureToastMessage(
      payload.type as string,
      payload.createdDate as string,
      t,
      payload.displayName || filename,
      i18n.language
    );

    let variant: FuelToastVariant;
    if (partialError) {
      variant = "warning";
    } else {
      variant = payload.compilationStatus === "COMPLETED" ? "success" : "error";
    }

    enqueueToast({
      variant,
      message:
        payload.compilationStatus === "FAILED"
          ? failureMessage
          : successMessage,
      isCloseable: true,
    });
    setNotifications(updatedAccountNotification.notifications);
    setAreNewNotificationsAvailable(
      updatedAccountNotification.areNewNotificationsAvailable
    );
  };

  useEffect(() => {
    const eventHandler = (e: Event) => newNotificationHandler(e, isMenuOpen);
    window.addEventListener(NOTIFY, eventHandler);
    return () => window.removeEventListener(NOTIFY, eventHandler);
  }, [accountId, isMenuOpen]);

  return {
    notifications,
    areNewNotificationsAvailable,
    markAllNotificationAsRead: () => {
      markAllNotificationAsReadInStorage(accountId);
      setAreNewNotificationsAvailable(false);
    },
    markNotificationsAsRead: (notificationIds: string[]) => {
      const updatedNotifications = markNotificationsAsReadInStorage(
        accountId,
        notificationIds
      );
      setNotifications([...updatedNotifications]);
    },
    expireNotification: (notificationId: number): void => {
      const updatedNotifications = expireNotificationInStorage(
        accountId,
        notificationId
      );
      setNotifications([...updatedNotifications]);
    },
    deleteNotification: (notificationId: number): void => {
      const updatedNotifications = deleteNotification(
        accountId,
        notificationId
      );
      setNotifications([...updatedNotifications]);
    },
  };
};

export default useNotifications;
